/**
 * DashboardRangeContext — shared selected date range for the home dashboard.
 *
 * The dropdown in the page header sets `rangeKey`; every widget hook reads
 * it via useDashboardRange() and includes it in its React Query cache key.
 * Changing the dropdown invalidates the affected queries and triggers a
 * refetch with the new range.
 *
 * The context has a safe default ('today' + no-op setter) so widget hooks
 * still work in tests or stories that render a single widget without
 * mounting the full provider tree.
 */

import * as React from "react";

export const DASHBOARD_RANGE_KEYS = [
  "today",
  "this_week",
  "this_month",
  "last_month",
] as const;

export type DashboardRangeKey = (typeof DASHBOARD_RANGE_KEYS)[number];

export const DEFAULT_DASHBOARD_RANGE: DashboardRangeKey = "today";

export const DASHBOARD_RANGE_LABELS: Record<DashboardRangeKey, string> = {
  today: "Today",
  this_week: "This Week",
  this_month: "This Month",
  last_month: "Last Month",
};

interface DashboardRangeContextValue {
  rangeKey: DashboardRangeKey;
  setRangeKey: (next: DashboardRangeKey) => void;
}

const DashboardRangeContext = React.createContext<DashboardRangeContextValue>({
  rangeKey: DEFAULT_DASHBOARD_RANGE,
  setRangeKey: () => {
    // No-op default — useful when a widget is rendered outside the
    // provider (tests, isolated stories). Production tree always mounts
    // DashboardRangeProvider above the widget grid.
  },
});

export function DashboardRangeProvider({
  children,
  initial = DEFAULT_DASHBOARD_RANGE,
}: {
  children: React.ReactNode;
  initial?: DashboardRangeKey;
}) {
  const [rangeKey, setRangeKey] = React.useState<DashboardRangeKey>(initial);
  const value = React.useMemo(() => ({ rangeKey, setRangeKey }), [rangeKey]);
  return (
    <DashboardRangeContext.Provider value={value}>
      {children}
    </DashboardRangeContext.Provider>
  );
}

/** Read the currently selected range key. */
export const useDashboardRange = (): DashboardRangeKey =>
  React.useContext(DashboardRangeContext).rangeKey;

/** Read + write the range key (use this in the header dropdown). */
export const useDashboardRangeControl = (): DashboardRangeContextValue =>
  React.useContext(DashboardRangeContext);
